import { Box, Typography } from '@mui/material';
import {
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineOppositeContent,
    TimelineDot,
    TimelineConnector,
    TimelineContent,
} from '@mui/lab';
import { colors, typography as typographyTokens, radii } from '@components/design-system';

import type { ComponentProps, ReactNode } from 'react';

type CutTimelineProps = {
    items: {
        content: string;
        label: string;
        description?: ReactNode;
        icon?: ReactNode;
        color?: ComponentProps<typeof TimelineDot>['color'];
    }[];
};

const colorMap: Record<string, { bg: string; border: string; icon: string }> = {
    info: {
        bg: colors.primary[100],
        border: colors.primary[300],
        icon: colors.primary[600],
    },
    success: {
        bg: colors.success.light,
        border: colors.success.main,
        icon: colors.success.dark,
    },
    secondary: {
        bg: colors.neutral[100],
        border: colors.neutral[300],
        icon: colors.neutral[600],
    },
    error: {
        bg: colors.error.light,
        border: colors.error.main,
        icon: colors.error.dark,
    },
    warning: {
        bg: colors.warning.light,
        border: colors.warning.main,
        icon: colors.warning.dark,
    },
};

export const CutTimeline = ({ items }: CutTimelineProps) => {
    return (
        <Timeline
            sx={{
                p: 0,
                m: 0,
                '& .MuiTimelineItem-root:before': {
                    flex: 0,
                    padding: 0,
                },
            }}>
            {items.map(({ content, label, icon, description, color = 'info' }, index) => {
                const colorSet = colorMap[color] || colorMap.info;

                return (
                    <TimelineItem key={`timeline-${content}-${label}`}>
                        <TimelineOppositeContent
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-end',
                                pt: 1.5,
                                flex: '0 0 140px',
                            }}>
                            <Typography
                                sx={{
                                    fontSize: typographyTokens.fontSize.sm,
                                    color: colors.neutral[500],
                                    textAlign: 'right',
                                }}>
                                {content}
                            </Typography>
                        </TimelineOppositeContent>
                        <TimelineSeparator>
                            <TimelineDot
                                sx={{
                                    backgroundColor: colorSet.bg,
                                    border: `2px solid ${colorSet.border}`,
                                    boxShadow: 'none',
                                    p: 1,
                                    m: 0,
                                    '& svg': {
                                        fontSize: 20,
                                        color: colorSet.icon,
                                    },
                                }}>
                                {icon}
                            </TimelineDot>
                            {index !== items.length - 1 && (
                                <TimelineConnector
                                    sx={{
                                        backgroundColor: colors.neutral[200],
                                        width: 2,
                                    }}
                                />
                            )}
                        </TimelineSeparator>
                        <TimelineContent
                            sx={{
                                py: 1,
                                px: 2,
                            }}>
                            <Box
                                sx={{
                                    backgroundColor: colors.neutral[50],
                                    borderRadius: `${radii.md}px`,
                                    border: `1px solid ${colors.neutral[200]}`,
                                    p: 1.5,
                                }}>
                                <Typography
                                    sx={{
                                        fontSize: typographyTokens.fontSize.sm,
                                        fontWeight: typographyTokens.fontWeight.semibold,
                                        color: colors.neutral[900],
                                        mb: description ? 0.5 : 0,
                                    }}>
                                    {label}
                                </Typography>
                                {description && (
                                    <Typography
                                        sx={{
                                            fontSize: typographyTokens.fontSize.sm,
                                            color: colors.neutral[600],
                                        }}>
                                        {description}
                                    </Typography>
                                )}
                            </Box>
                        </TimelineContent>
                    </TimelineItem>
                );
            })}
        </Timeline>
    );
};
